import axios from 'axios';
import { supabase } from '../supabase';
import { User } from '../utils/types';
import { Session } from '@supabase/supabase-js';

export const fetchUserProfile = async (userId: string) => {
  try {
    // Obtener el perfil del usuario
    // Fetch only the user row first (avoid join to reduce RLS interactions)
    console.log('userId:', userId);
    const { data: userRow, error: userError } = await supabase
      .from('users')
      .select('*')
      .eq('auth_id', userId)
      .is('deleted_at', null)
      .single();

    if (userError) {
      console.error('Error fetching user profile:', userError);
      throw userError;
    }
    console.log('userRow:', userRow);

    // Eager-load exporter separately to avoid cross-table policy conflicts
    let userData: any = userRow;
    if (userRow?.exporter_id) {
      const { data: exporter, error: exporterError } = await supabase
        .from('exporters')
        .select('*')
        .eq('id', userRow.exporter_id)
        .single();

      if (!exporterError && exporter) {
        userData = { ...userRow, exporter };
      }
    }

    // Eager-load importer separately
    console.log('userRow.importer_id:', userRow?.importer_id);
    if (userRow?.importer_id) {
      const { data: importer, error: importerError } = await supabase
        .from('importers')
        .select('*')
        .eq('id', userRow.importer_id)
        .single();

      console.log('importer fetched:', importer, 'error:', importerError);
      if (!importerError && importer) {
        userData = { ...userData, importer };
      }
    }

    // Si el usuario tiene rol 'basic', obtener sus permisos
    if (userData && userData.roles && userData.roles.includes('basic')) {
      // Obtener permisos del usuario
      const { data: permissions, error: permissionsError } = await supabase
        .from('user_permissions')
        .select('*')
        .eq('user_id', userData.id);

      if (permissionsError) {
        console.error('Error fetching permissions:', permissionsError);
        // No lanzamos error para no interrumpir el flujo, seguimos sin permisos
      }

      // Si hay permisos, agregarlos al perfil del usuario
      if (permissions && permissions.length > 0) {
        console.log('Found permissions for user:', permissions.length);
        return {
          ...userData,
          permissions: permissions.map((p) => ({
            sidebarItemKey: p.sidebar_item_key,
            enabled: p.enabled,
            title: p.title,
          })),
        };
      } else {
        console.log('No permissions found for user');
      }
    }

    return userData;
  } catch (error: any) {
    console.error('Error in fetchUserProfile:', error);
    console.error('Error message:', error?.message);
    console.error('Error code:', error?.code);
    throw error;
  }
};

export const getAllUsers = async ({ archived = false }: { archived?: boolean } = {}) => {
  try {
    let query = supabase
      .from('users')
      .select('*, exporter:exporter_id(*), importer:importer_id(*)');

    query = archived
      ? query.or('deleted_at.not.is.null,auth_id.is.null')
      : query.is('deleted_at', null).not('auth_id', 'is', null);

    const { data: users, error } = await query;

    if (error) throw error;

    if (!users || users.length === 0) return [];

    // Obtener permisos para usuarios básicos
    const basicUserIds = users
      .filter((user) => user.roles.includes('basic'))
      .map((user) => user.id);

    if (basicUserIds.length > 0) {
      const { data: allPermissions, error: permissionsError } = await supabase
        .from('user_permissions')
        .select('*')
        .in('user_id', basicUserIds);

      if (permissionsError) throw permissionsError;

      // Agregar permisos a los usuarios básicos
      return users.map((user) => {
        if (user.roles.includes('basic')) {
          const userPermissions =
            allPermissions?.filter((p) => p.user_id === user.id) || [];
          return {
            ...user,
            permissions: userPermissions.map((p) => ({
              sidebarItemKey: p.sidebar_item_key,
              enabled: p.enabled,
              title: p.title,
            })),
          };
        }
        return user;
      });
    }

    return users;
  } catch (error) {
    console.error('Error fetching all users:', error);
    throw new Error('Failed to fetch all users');
  }
};

export const updateUser = async (
  userId: number,
  userData: Partial<User>,
  _session?: Session | null
) => {
  try {
    // Build update payload
    const payload: Record<string, any> = {};
    if (userData.first_name !== undefined) payload.first_name = userData.first_name;
    if (userData.last_name !== undefined) payload.last_name = userData.last_name;
    if (userData.roles !== undefined) payload.roles = userData.roles;
    if ((userData as any).exporter_id !== undefined)
      payload.exporter_id = (userData as any).exporter_id ?? null;
    if ((userData as any).importer_id !== undefined)
      payload.importer_id = (userData as any).importer_id ?? null;
    if (userData.demo_access_until !== undefined)
      payload.demo_access_until = userData.demo_access_until || null;

    let updatedUser: any = null;
    if (Object.keys(payload).length > 0) {
      const { data, error } = await supabase
        .from('users')
        .update(payload)
        .eq('id', userId)
        .select('*')
        .single();
      if (error) throw error;
      updatedUser = data;
    } else {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
      if (error) throw error;
      updatedUser = data;
    }

    // Handle permissions for basic role when provided
    if (
      Array.isArray(userData.roles) &&
      userData.roles.includes('basic') &&
      Array.isArray((userData as any).permissions)
    ) {
      const newPerms = (userData as any).permissions as Array<{ sidebarItemKey: string; enabled: boolean; title: string }>;

      // Replace existing permissions
      const { error: delErr } = await supabase
        .from('user_permissions')
        .delete()
        .eq('user_id', userId);
      if (delErr) throw delErr;

      if (newPerms.length > 0) {
        const rows = newPerms.map((p) => ({
          user_id: userId,
          sidebar_item_key: p.sidebarItemKey,
          enabled: p.enabled,
          title: p.title,
        }));
        const { error: insErr } = await supabase.from('user_permissions').insert(rows);
        if (insErr) throw insErr;
      }
    }

    return updatedUser as User;
  } catch (error) {
    console.error('Error updating user:', error);
    throw new Error('Failed to update user');
  }
};

export const createUser = async (
  userData: Partial<User>,
  session?: Session | null,
  options: { sendWelcomeEmail?: boolean } = {}
) => {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-user`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          email: userData.email,
          password: userData.password,
          first_name: userData.first_name,
          last_name: userData.last_name,
          roles: userData.roles,
          exporter_id: userData.exporter?.id,
          importer_id: userData.importer?.id,
          permissions: userData.permissions || [],
          send_welcome_email: options.sendWelcomeEmail === true,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.error || 'Failed to create user');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating user:', error);
    // El motivo que devuelve create-user, para que la pantalla lo muestre.
    throw error instanceof Error ? error : new Error('Failed to create user');
  }
};

export const deleteUser = async (userId: number, session?: Session | null) => {
  try {
    // The Edge Function deletes Auth access and archives the profile.
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          user_id: userId,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error);
    }
  } catch (error) {
    console.error('Error deleting user:', error);
    throw new Error('Failed to delete user');
  }
};

export const changeUserPassword = async (
  authId: string,
  newPassword: string,
  session?: Session | null
) => {
  try {
    const { data } = await axios.post(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/change-password`,
      {
        auth_id: authId,
        new_password: newPassword,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
      }
    );
    return data;
  } catch (error) {
    console.error('Error changing password:', error);
    throw new Error('Failed to change password');
  }
};

/** Marca una novedad de la plataforma como vista por el usuario actual. */
export const markAnnouncementSeen = async (id: string): Promise<void> => {
  const { error } = await supabase.rpc('mark_announcement_seen', { p_id: id });
  if (error) throw new Error(error.message);
};
